'use client';

import type { IconName } from '@/components/entities/Sprite/Icon';
import type { ModalNameType } from '@/modals/components/ModalProvider';
import { useParams } from 'next/navigation';
import { useEffect, useState } from 'react';
import DailyBonus from '@/components/entities/dailyBonus';
import { ACCOUNT_LINKS, PROTECTED_LINKS } from '@/components/features/layout/Navigation/consts/links';

import { useSidebarGroupCategories } from '@/hooks/useSideBarGroupCategories';
import { usePathname } from '@/i18n/routing';
import CategoryItem from './components/CategoryItem';
import NavigationItem from './components/NavigationItem';
import OtherCategoriesAccordion from './components/OtherCategoriesAccordion';

interface Props {
  isMenuMinify: boolean;
  handleCloseSideBar: () => void;
}

const NavigationItems = ({ isMenuMinify, handleCloseSideBar }: Props) => {
  const [activeId, setActiveId] = useState<string | null>(null);
  const pathname = usePathname();
  const categoryId = useParams()?.id as string;

  const { beforeFavoritesCategories, afterFavoritesCategories, otherCategories } = useSidebarGroupCategories();

  useEffect(() => {
    const allLinks = [...PROTECTED_LINKS, ...ACCOUNT_LINKS].filter(link => link.href);
    const isLinkActive = allLinks.some(link => pathname === link.href);

    if (!isLinkActive) {
      setActiveId(null);
    }
  }, [pathname]);

  return (
    <div className="sticky top-0 md:pb-4 lg:pb-16 pt-4 px-2.5 lg:px-2 max-md:pt-16 flex flex-col lg:pt-6">

      <div className="flex flex-col">
        <div className="flex flex-col order-1 lg:order-2 lg:gap-1 lg:mt-px mt-4 gap-1 3xl:px-5">
          {PROTECTED_LINKS.map(link => (
            <NavigationItem
              key={link.label}
              label={link.label}
              icon={link.icon as IconName}
              href={link.href}
              modal={link.modal as ModalNameType}
              badge={link.badge}
              variant="primary"
              activeId={activeId}
              setActiveId={setActiveId}
              handleCloseSideBar={handleCloseSideBar}
            />
          ))}
          <div className="mt-4 h-px bg-c300 opacity-30 -mx-2.5 lg:-mx-2 3xl:-mx-7" />
        </div>
        <div className="3xl:px-2.5">
          <DailyBonus />
        </div>
      </div>
      <div className="flex flex-col mt-4 gap-1 relative lg:gap-1 lg:mt-4 3xl:px-5">
        {beforeFavoritesCategories?.map(category => (
          <CategoryItem
            key={category.id}
            category={category}
            categoryId={categoryId}
            handleCloseSideBar={handleCloseSideBar}
            isMenuMinify={isMenuMinify}
          />
        ))}
        <NavigationItem
          label="Favorites"
          icon="favorites"
          href="/category/favorites-games"
          variant="account"
          isActive={pathname === '/category/favorites-games'}
          handleCloseSideBar={handleCloseSideBar}
        />

        {afterFavoritesCategories?.map(category => (
          <CategoryItem
            key={category.id}
            category={category}
            categoryId={categoryId}
            handleCloseSideBar={handleCloseSideBar}
            isMenuMinify={isMenuMinify}
          />
        ))}
        {otherCategories && otherCategories.length > 0 && (
          <OtherCategoriesAccordion
            categoryId={categoryId}
            handleCloseSideBar={handleCloseSideBar}
            isMenuMinify={isMenuMinify}
            otherCategories={otherCategories}
          />
        )}
      </div>

      <div className="flex flex-col gap-1 mt-3.75 max-md:pb-5.5 lg:mt-1.75 3xl:mt-4 3xl:px-5">
        {ACCOUNT_LINKS.map(link => (
          <NavigationItem
            key={link.label}
            label={link.label}
            icon={link.icon as IconName}
            href={link.href}
            modal={link.modal as ModalNameType}
            variant="account"
            activeId={activeId}
            setActiveId={setActiveId}
            handleCloseSideBar={handleCloseSideBar}
          />
        ))}
      </div>
    </div>
  );
};

export default NavigationItems;
